import type { TicketGroup } from '@interfaces/ticketevolution/tikets';
import { normalizeSectionId } from './section-ids';
import {
  availableZoneBuckets,
  getZoneBucket,
  isAccessibleListing,
  isElectronicListing,
  percentilePrice,
  type ZoneBucket,
} from './ticket-ranking';
import { INITIAL_TICKET_PRICE_RANGE } from '@store/tickets-store';
import {
  boundTicketPriceRange as clampPriceRangeToBounds,
  type DualRangeValue,
} from '@v2/ui/Slider/range-slider-math';
import {
  getTicketPriceRange,
  type TicketPriceRange,
} from '../components/ticket-price-distribution';

export function boundTicketPriceRange(
  current: DualRangeValue,
  bounds: TicketPriceRange
): DualRangeValue {
  return clampPriceRangeToBounds(current, bounds, INITIAL_TICKET_PRICE_RANGE);
}

export type TicketFilterPolicy = {
  min: number;
  max: number;
  ticketsQuantity: number;
  isOnlyElectronic: boolean;
  zoneBuckets: ZoneBucket[];
  accessibleOnly: boolean;
  underPriceCap: number | null;
  selectedGroups: string[] | null;
};

export type TicketFilterOptions = {
  zones: ZoneBucket[];
  underCap: number | null;
  hasAda: boolean;
  fullPriceRange: TicketPriceRange;
};

export function getTicketFilterOptions(
  list: TicketGroup[]
): TicketFilterOptions {
  return {
    zones: availableZoneBuckets(list),
    underCap: getUnderPriceCapOption(list),
    hasAda: list.some(isAccessibleListing),
    fullPriceRange: getTicketPriceRange(list),
  };
}

export function getUnderPriceCapOption(list: TicketGroup[]): number | null {
  if (!list.length) return null;
  return Math.max(25, Math.ceil(percentilePrice(list, 0.25) / 5) * 5);
}

export function countActiveTicketFilters({
  zoneBuckets,
  accessibleOnly,
  underPriceCap,
  isOnlyElectronic,
  selectedGroups,
  hasPriceRangeFilter,
  sortIsDefault,
  includeSortCount,
}: {
  zoneBuckets: ZoneBucket[];
  accessibleOnly: boolean;
  underPriceCap: number | null;
  isOnlyElectronic: boolean;
  selectedGroups: string[];
  hasPriceRangeFilter: boolean;
  sortIsDefault: boolean;
  includeSortCount: boolean;
}): number {
  return (
    zoneBuckets.length +
    (accessibleOnly ? 1 : 0) +
    (underPriceCap != null ? 1 : 0) +
    (isOnlyElectronic ? 1 : 0) +
    (selectedGroups.length > 0 ? 1 : 0) +
    (hasPriceRangeFilter ? 1 : 0) +
    (includeSortCount && !sortIsDefault ? 1 : 0)
  );
}

export function applyTicketFilterPolicy(
  list: TicketGroup[],
  policy: TicketFilterPolicy
): TicketGroup[] {
  let filtered = list.filter((item) => {
    return (
      item.retail_price >= policy.min &&
      item.retail_price <= policy.max &&
      item.quantity >= policy.ticketsQuantity &&
      item.splits.includes(policy.ticketsQuantity) &&
      (!policy.isOnlyElectronic || isElectronicListing(item))
    );
  });

  if (policy.selectedGroups && policy.selectedGroups.length > 0) {
    const selectedIds = new Set(
      policy.selectedGroups.map((sectionId) => normalizeSectionId(sectionId))
    );
    const uniqueSections = [
      ...new Set(
        list
          .filter((item) =>
            selectedIds.has(normalizeSectionId(item.tevo_section_name || ''))
          )
          .map((item) => item.section)
      ),
    ];
    filtered = filtered.filter((item) => uniqueSections.includes(item.section));
  }

  if (policy.zoneBuckets.length > 0) {
    filtered = filtered.filter((item) =>
      policy.zoneBuckets.includes(getZoneBucket(item.tevo_section_name))
    );
  }

  if (policy.accessibleOnly) {
    filtered = filtered.filter(isAccessibleListing);
  }

  if (policy.underPriceCap != null) {
    const cap = policy.underPriceCap;
    filtered = filtered.filter((item) => item.retail_price <= cap);
  }

  return filtered;
}
